import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Form } from "@unform/web";
import { FormHandles } from "@unform/core";

import { CurrencyInput, Input, Modal } from "../../../../components/global";
import { ModalHandles } from "../../../../components/global/modal";
import { useInvalidateCache } from "../../../../shared/hooks/useInvalidateCache";
import { showToast } from "../../../../components/global/toast";
import { semvApi } from "../../../../shared/services/axios/apis.service";
import { ApiErrorHandler } from "../../../../shared/utils/errors.utils";
import { yupValidation } from "../../../../shared/utils/yup";
import { projectCreationSchema } from "./schema";
import { SingleDatePicker } from "../../../../components/global/single-date-picker";

export type CreateSecretaryProjectModalHandles = {
    open: (secretary$: number) => void;
    close: () => void;
};

const CreateSecretaryProject = forwardRef<CreateSecretaryProjectModalHandles>(
    (_, ref) => {
        const invalidate = useInvalidateCache();

        const [loading, setLoading] = useState(false);
        const [secretary, setSecretary] = useState<number>();
        const innerRef = useRef<ModalHandles>(null);
        const formRef = useRef<FormHandles>(null);

        const handleClose = () => {
            setSecretary(undefined);
            innerRef.current?.close();
        };

        useImperativeHandle(ref, () => ({
            close: handleClose,
            open: (secretary$) => {
                setSecretary(secretary$);
                innerRef.current?.open();
            },
        }));

        const handleSubmit = async (formData: {
            name: string;
            description: string;
            amount: number;
            observation?: string;
            deadline: Date;
        }) => {
            try {
                setLoading(true);

                await projectCreationSchema.validate(formData, {
                    abortEarly: false,
                });

                await semvApi.post(
                    `/government-plan/projects`,
                    Object.assign(formData, { secretaryId: secretary })
                );

                showToast({
                    type: "success",
                    message: "Projeto cadastrado com sucesso",
                });

                invalidate();
                handleClose();
            } catch (err) {
                ApiErrorHandler(err);
                formRef.current?.setErrors(yupValidation(err));
            } finally {
                setLoading(false);
            }
        };

        return (
            <Modal ref={innerRef} title="Cadastro de projeto" size="lg">
                <Form onSubmit={handleSubmit} ref={formRef}>
                    <div className="grid gap-y-6 gap-x-2 grid-cols-3">
                        <div className="col-span-3">
                            <Input
                                name="name"
                                label="Nome"
                                className="form-input"
                            />
                        </div>
                        <div className="col-span-3">
                            <Input
                                name="description"
                                label="Descrição"
                                className="form-input"
                            />
                        </div>

                        <CurrencyInput
                            name="amount"
                            label="Valor para executar o projeto"
                        />

                        <SingleDatePicker name="deadline" label="Data limite" />

                        <div className="col-span-3">
                            <Input
                                name="observation"
                                label="Observação"
                                className="form-input"
                            />
                        </div>
                    </div>
                    <div className="flex justify-between">
                        <div />
                        <button
                            className="btn-primary"
                            type="submit"
                            disabled={loading}
                        >
                            Salvar
                        </button>
                    </div>
                </Form>
            </Modal>
        );
    }
);

CreateSecretaryProject.displayName = "CreateSecretaryProject";

export { CreateSecretaryProject };
